import React, { useState, useEffect } from 'react';
import Joyride, { ACTIONS, EVENTS, STATUS } from 'react-joyride';
import { Sparkles, Navigation, MessageSquare, TrendingUp, Cpu, HardDrive, Database } from 'lucide-react';

const TutorialManager = ({ setActiveTab, activeTab }) => {
    const [run, setRun] = useState(false);
    const [stepIndex, setStepIndex] = useState(0);

    useEffect(() => {
        // Check if tutorial has already been completed
        const isCompleted = localStorage.getItem('tutorial_completed');

        if (!isCompleted) {
            // Small delay to ensure everything is loaded
            const timer = setTimeout(() => {
                setRun(true);
            }, 1000);
            return () => clearTimeout(timer);
        }
    }, []);

    const handleJoyrideCallback = (data) => {
        const { action, index, status, type } = data;

        if ([EVENTS.STEP_AFTER, EVENTS.TARGET_NOT_FOUND].includes(type)) {
            // Update state to advance the tour
            const nextStepIndex = index + (action === ACTIONS.PREV ? -1 : 1);
            setStepIndex(nextStepIndex);

            // Handle interactive navigations
            // Note: Index numbers shifted because we removed "Quick Actions"
            if (action === ACTIONS.PREV && nextStepIndex === 1) {
                // Back to Home from MI Tools
                setActiveTab('home');
            } else if (action === ACTIONS.NEXT && nextStepIndex === 4) {
                // Switching to Trends
                setActiveTab('trends');
            } else if (action === ACTIONS.NEXT && nextStepIndex === 5) {
                // Switching to Predictions
                setActiveTab('predictions');
            } else if (action === ACTIONS.NEXT && nextStepIndex === 6) {
                // Switching to Storage
                setActiveTab('storage');
            }
        } else if ([STATUS.FINISHED, STATUS.SKIPPED].includes(status)) {
            setRun(false);
            localStorage.setItem('tutorial_completed', 'true');
        }
    };

    const steps = [
        // 1. Welcome
        {
            target: 'body',
            placement: 'center',
            content: (
                <div className="text-left">
                    <div className="flex items-center gap-2 mb-3">
                        <div className="p-2 bg-blue-100 rounded-lg">
                            <Sparkles className="w-6 h-6 text-blue-600" />
                        </div>
                        <h3 className="text-xl font-bold text-gray-900">Welcome to Maintenance Agent</h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed mb-4">
                        Let's take a comprehensive tour of your new AI-powered maintenance platform. We'll show you how to analyze data, generate reports, and manage files.
                    </p>
                </div>
            ),
            disableBeacon: true,
        },
        // 2. Navigation Overview
        {
            target: '[data-tour-id="nav-home"]',
            placement: 'bottom',
            content: (
                <div className="text-left">
                    <div className="flex items-center gap-2 mb-2">
                        <Navigation className="w-5 h-5 text-blue-600" />
                        <h4 className="font-bold text-gray-900">Dashboard & Navigation</h4>
                    </div>
                    <p className="text-gray-600 text-sm">
                        Navigate between different modules here. We'll explore each of them in a moment.
                    </p>
                </div>
            ),
            disableBeacon: true,
        },
        // 3. Maintenance Intelligence Tools (How to use + Export)
        {
            target: '[data-tour-id="mi-section"]',
            placement: 'top',
            content: (
                <div className="text-left">
                    <div className="flex items-center gap-2 mb-2">
                        <Database className="w-5 h-5 text-blue-600" />
                        <h4 className="font-bold text-gray-900">Analysis Tools</h4>
                    </div>
                    <p className="text-gray-600 text-sm mb-3">
                        Select a tool card here (like <strong>Fleet Event Counts</strong>) to open its analysis form.
                    </p>
                    <div className="bg-gray-50 p-3 rounded-lg border border-gray-100">
                        <h5 className="font-semibold text-gray-700 text-xs mb-1 uppercase">How to Export & Report:</h5>
                        <ol className="list-decimal pl-4 text-xs text-gray-600 space-y-1">
                            <li>Fill in the form parameters</li>
                            <li>Click <strong>Run Analysis</strong></li>
                            <li>Once results appear, look for the <strong className="text-green-600">Export Excel</strong> and <strong className="text-red-600">Save Report</strong> buttons at the top of the results.</li>
                        </ol>
                    </div>
                </div>
            ),
            disableBeacon: true,
        },
        // 4. Chat Assistant
        {
            target: '[data-tour-id="chat-input"]',
            content: (
                <div className="text-left">
                    <div className="flex items-center gap-2 mb-2">
                        <MessageSquare className="w-5 h-5 text-blue-600" />
                        <h4 className="font-bold text-gray-900">AI Assistant</h4>
                    </div>
                    <p className="text-gray-600 text-sm">
                        Chat directly with your data. You can also upload files here for instant analysis using the paperclip icon.
                    </p>
                </div>
            ),
            placement: 'left',
            disableBeacon: true,
        },
        // 5. Trends
        {
            target: '[data-tour-id="nav-trends"]',
            placement: 'bottom',
            content: (
                <div className="text-left">
                    <div className="flex items-center gap-2 mb-2">
                        <TrendingUp className="w-5 h-5 text-blue-600" />
                        <h4 className="font-bold text-gray-900">Trend Analysis</h4>
                    </div>
                    <p className="text-gray-600 text-sm">
                        We've switched you to the <strong>Trends</strong> tab. Use this for deep-dives into long-term performance data.
                    </p>
                </div>
            ),
            disableBeacon: true,
        },
        // 6. Predictions
        {
            target: '[data-tour-id="nav-predictions"]',
            placement: 'bottom',
            content: (
                <div className="text-left">
                    <div className="flex items-center gap-2 mb-2">
                        <Cpu className="w-5 h-5 text-blue-600" />
                        <h4 className="font-bold text-gray-900">Predictive Maintenance</h4>
                    </div>
                    <p className="text-gray-600 text-sm">
                        See into the future. This tab shows AI predictions for likely component failures in the coming weeks.
                    </p>
                </div>
            ),
            disableBeacon: true,
        },
        // 7. Storage (New)
        {
            target: '[data-tour-id="nav-storage"]',
            placement: 'bottom',
            content: (
                <div className="text-left">
                    <div className="flex items-center gap-2 mb-2">
                        <HardDrive className="w-5 h-5 text-blue-600" />
                        <h4 className="font-bold text-gray-900">Report Storage</h4>
                    </div>
                    <p className="text-gray-600 text-sm">
                        Finally, access all your saved PDF reports, exported Excel files, and uploaded documents here.
                    </p>
                    <div className="mt-2 text-xs text-gray-500">
                        Reports generated from the dashboard are automatically saved to the <strong>Reports</strong> folder.
                    </div>
                </div>
            ),
            disableBeacon: true,
        }
    ];

    return (
        <Joyride
            steps={steps}
            run={run}
            stepIndex={stepIndex}
            continuous
            showSkipButton
            showProgress
            callback={handleJoyrideCallback}
            spotlightClicks={true}
            scrollOffset={100}
            styles={{
                options: {
                    zIndex: 10000,
                    primaryColor: '#2563EB',
                    textColor: '#1F2937',
                    backgroundColor: '#ffffff',
                    arrowColor: '#ffffff',
                    overlayColor: 'rgba(0, 0, 0, 0.4)',
                },
                tooltip: {
                    borderRadius: '16px',
                    boxShadow: '0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04)',
                    padding: '20px',
                },
                buttonNext: {
                    backgroundColor: '#2563EB',
                    color: '#ffffff',
                    borderRadius: '8px',
                    outline: 'none',
                    padding: '10px 24px',
                    fontWeight: 600,
                    boxShadow: '0 4px 6px -1px rgba(37, 99, 235, 0.2)',
                    transition: 'all 0.2s',
                }
            }}
            floaterProps={{
                hideArrow: false,
                disableAnimation: true,
            }}
        />
    );
};

export default TutorialManager;
